import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, ChevronDown, Command as CommandIcon, KeyRound, Lock, LogOut, Monitor, Moon, Rows3, Rows4, Sparkles, Sun } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router";
import type { Me } from "../lib/types";
import type { LiveState } from "../lib/live";
import { densityStore, HELP_LEVEL_OPTIONS, helpLevelStore, setDensity, setHelpLevel, setTheme, themeStore, type ThemePref } from "../lib/prefs";
import { useSaveSummary } from "../lib/saveState";
import { initials } from "../lib/format";
import { logout } from "../pages/AuthPages";
import { cx, Tip, Wordmark } from "./ui";
import { hasCap, hasPipeline, isAssignedOnly, isRmSupport, realRole, roleLabel } from "../lib/roles";
import { paletteWorkspace, prospectingNav, scenariosNav, workspacesFor, type Workspace } from "../lib/crm";
import { HelpMenu, RoleSwitcher } from "./ActingRole";
import { FeedbackButton } from "./feedback/FeedbackLayer";
import { PreviewChip } from "./feedback/parts";
import { previewOn } from "../lib/preview";

export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Tooltip text, also read as the link's description. */
  hint?: string;
  /** Served by the server, not the SPA router: the link does a full page load. */
  reloadDocument?: boolean;
  /** A screen behind "Preview new screens": shown only with Preview on, with a Preview chip. */
  preview?: boolean;
}

/** The Four Legs desk, served at `/desk/` by the staff app (apps/pipeline_api/src/desk.rs). */
export const DESK_NAV: NavItem = { to: "/desk/", label: "Desk", hint: "Start a file and price scenarios", reloadDocument: true };

export function navItems(me: Me): NavItem[] {
  if (!hasPipeline(me)) return [];
  const items: NavItem[] = [
    { to: "/role", label: "Role home" },
    { to: "/pipeline", label: isRmSupport(me) ? "Pipeline" : "My Pipeline" },
    ...(me.user.desk_available ? [DESK_NAV] : []),
    { to: "/los", label: "LSAI LOS" },
    ...(!isAssignedOnly(me) ? [{ to: "/team", label: "Team" }, { to: "/goals", label: "Goals" }] : []),
  ];
  if (hasCap(me, "manage_team_members") || hasCap(me, "view_unassigned_members")) items.push({ to: "/people", label: "People" });
  if (hasCap(me, "manage_users")) items.push({ to: "/admin", label: "Admin" });
  if (hasCap(me, "view_security_audit")) items.push({ to: "/security", label: "Security & Audit" });
  if (!isAssignedOnly(me)) items.push({ to: "/broker-links", label: "Broker links" });
  if (hasCap(me, "request_lo_signature")) items.push({ to: "/signatures", label: "LO signatures" });
  if (hasCap(me, "view_conditions")) items.push({ to: "/conditions", label: "Conditions" });
  items.push({ to: "/smart-drops", label: "Smart Drops" });
  items.push({ to: "/core-workspace", label: "Core workspace", hint: "Fees, rates, pre-qual, bank income, loan graph and scenarios" });
  // Preview screens last, so the everyday nav keeps its place.
  return [...items, ...previewItems(me)];
}

/** Screens behind the Preview switch. */
export function previewItems(me: Me): NavItem[] {
  if (!previewOn(me)) return [];
  return [{ to: "/feedback", label: "Feedback", hint: "All feedback pins (Preview)", preview: true }];
}

const WORKSPACE: Record<Workspace, { label: string; to: string }> = {
  prospecting: { label: "Prospecting", to: "/prospecting" },
  scenarios: { label: "Scenarios", to: "/scenarios" },
  deals: { label: "Active deals", to: "/pipeline" },
};

/** Workspaces behind one sign-in (D25): green Prospecting, then navy Scenarios and Active deals. */
function WorkspaceSwitch({ me, current }: { me: Me; current: Workspace }) {
  const spaces = workspacesFor(me);
  if (spaces.length < 2) {
    return <span className="hidden text-[13px] font-semibold text-white/85 sm:inline">{WORKSPACE[current].label}</span>;
  }
  return (
    <div role="tablist" aria-label="Workspace" className="flex shrink-0 rounded-md bg-black/20 p-0.5">
      {spaces.map((w) => (
        <NavLink
          key={w}
          role="tab"
          aria-selected={w === current}
          to={WORKSPACE[w].to}
          className={cx(
            "focus-ring rounded-[5px] px-2.5 py-1 text-[13px] font-semibold whitespace-nowrap",
            w === current ? "bg-white text-navy-900" : "text-white/80 hover:text-white",
          )}
        >
          {WORKSPACE[w].label}
        </NavLink>
      ))}
    </div>
  );
}

/** Where fixed panels under the top nav start: the header is two rows on phones (a 56px bar and the
 * 44px nav row) and one 56px bar from `sm` on. Keep in step with the header's heights below. */
export const BELOW_TOP_NAV = "top-[100px] sm:top-14";

export function TopNav({ me, live, onPalette }: { me: Me; live: LiveState; onPalette: () => void }) {
  const { pathname } = useLocation();
  const ws: Workspace = paletteWorkspace(me, pathname);
  const items: NavItem[] =
    ws === "prospecting" ? prospectingNav(me) : ws === "scenarios" ? scenariosNav(me) : navItems(me).map((i) => ({ ...i, end: false }));
  return (
    <header
      data-workspace={ws}
      className={cx(
        "sticky top-0 z-30 flex h-[100px] shrink-0 flex-wrap items-center gap-x-2 px-3 text-white sm:h-14 sm:flex-nowrap md:gap-3 md:px-4 dark:border-b dark:border-line",
        ws === "prospecting" ? "bg-green-900" : "bg-nav",
      )}
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:rounded focus:bg-surface focus:px-3 focus:py-1 focus:text-ink">
        Skip to content
      </a>
      <NavLink to={WORKSPACE[ws].to} className="focus-ring shrink-0 rounded-sm" aria-label="LendSure Pipeline home">
        <span className="hidden sm:inline">
          <Wordmark />
        </span>
        <span className="font-display text-[18px] font-bold sm:hidden">
          <span className="text-green-500">L</span>S
        </span>
      </NavLink>
      <WorkspaceSwitch me={me} current={ws} />
      {ws !== "prospecting" ? (
        <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/85 2xl:inline-flex" title="This side holds borrower data">
          <Lock size={11} aria-hidden /> borrower data
        </span>
      ) : null}
      {/* Full header height keeps the active underline inside the nav; narrow screens still scroll sideways with a subtle scrollbar.
          Phones put the nav on its own full-width row, so the links never shrink to nothing. */}
      <nav aria-label="Main" className="order-last flex h-11 w-full min-w-0 items-center gap-0.5 overflow-x-auto overflow-y-hidden [scrollbar-color:rgba(255,255,255,0.35)_transparent] [scrollbar-width:thin] sm:order-none sm:h-auto sm:w-auto sm:self-stretch">
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            reloadDocument={i.reloadDocument}
            title={i.hint}
            className={({ isActive }) =>
              cx(
                "focus-ring relative rounded-md px-2 py-1 text-[13px] font-medium whitespace-nowrap transition-colors",
                isActive ? "bg-white/12 text-white after:absolute after:inset-x-2 after:-bottom-[6px] after:h-[3px] after:rounded-full after:bg-green-500 sm:after:-bottom-[9px]" : "text-white/80 hover:bg-white/10 hover:text-white",
              )
            }
          >
            {i.label}
            {i.preview ? <PreviewChip onDark /> : null}
          </NavLink>
        ))}
      </nav>
      {/* On phones this sets the first row to 56px, so the header is exactly 56 + 44 (BELOW_TOP_NAV). */}
      <div className="ml-auto flex h-14 items-center gap-2 sm:h-auto">
        <SaveIndicator />
        <RoleSwitcher me={me} />
        <ConnectionPill state={live} />
        <FeedbackButton />
        <Tip content="Find a loan or jump anywhere (⌘K)" side="bottom">
          <button onClick={onPalette} className="focus-ring hidden items-center gap-1 rounded-md border border-white/25 px-2 py-1 text-[12px] text-white/85 hover:bg-white/10 md:inline-flex" aria-label="Open command palette">
            <CommandIcon size={13} /> K
          </button>
        </Tip>
        <HelpMenu me={me} />
        <UserMenu me={me} />
      </div>
    </header>
  );
}

function SaveIndicator() {
  const s = useSaveSummary();
  if (s.errors) return <span className="hidden text-[12px] font-medium text-[#FFC266] md:inline">⚠ {s.errors} not saved</span>;
  if (s.saving) return <span className="hidden text-[12px] text-white/80 md:inline">Saving…</span>;
  if (s.lastSavedAt) return <span className="hidden text-[12px] text-white/80 md:inline">✓ Saved</span>;
  return null;
}

export function ConnectionPill({ state }: { state: LiveState }) {
  const map: Record<LiveState, { label: string; dot: string; tip: string }> = {
    live: { label: "Live", dot: "bg-green-400", tip: "Connected: changes from your team appear instantly." },
    connecting: { label: "Connecting", dot: "bg-white/60 pulse-dot", tip: "Connecting to live updates…" },
    reconnecting: { label: "Reconnecting", dot: "bg-[#FFC266] pulse-dot", tip: "Lost the live connection. Your edits still save; reconnecting…" },
    offline: { label: "Offline", dot: "bg-[#FF8A80]", tip: "You're offline. Edits will fail until the connection is back." },
  };
  const m = map[state];
  return (
    <Tip content={m.tip} side="bottom">
      <span role="status" tabIndex={0} className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[12px] font-medium text-white">
        <span className={cx("h-2 w-2 rounded-full", m.dot)} aria-hidden />
        <span className="max-sm:sr-only">{m.label}</span>
      </span>
    </Tip>
  );
}

function UserMenu({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const theme = themeStore.use();
  const density = densityStore.use();
  const helpLevel = helpLevelStore.use();
  const item = "focus-ring flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[14px] text-ink outline-none data-[highlighted]:bg-hover";
  const themes: { v: ThemePref; label: string; icon: React.ReactNode }[] = [
    { v: "system", label: "System", icon: <Monitor size={14} /> },
    { v: "light", label: "Light", icon: <Sun size={14} /> },
    { v: "dark", label: "Dark", icon: <Moon size={14} /> },
  ];
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="focus-ring flex items-center gap-1.5 rounded-full py-0.5 pr-1.5 pl-0.5 hover:bg-white/10" aria-label="Account and settings">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-[12px] font-bold text-navy-950">{initials(me.user.display_name)}</span>
        <ChevronDown size={14} className="text-white/80" />
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={8} className="z-50 w-64 rounded-[10px] border border-line bg-raised p-1 shadow-[var(--shadow-2)]">
          <div className="px-2 py-2">
            <div className="font-medium text-ink">{me.user.display_name}</div>
            <div className="truncate text-[12px] text-ink-3">{me.user.email}</div>
            <div className="mt-0.5 text-[12px] text-ink-2">
              {roleLabel(realRole(me))}
              {me.user.team_name ? ` · ${me.user.team_name}` : ""}
            </div>
            {me.user.acting ? <div className="mt-0.5 text-[12px] font-semibold text-warning">Acting as {me.user.acting.label.toLowerCase()}</div> : null}
          </div>
          <Dropdown.Separator className="my-1 h-px bg-subtle" />
          <Dropdown.Label className="eyebrow px-2 py-1 text-ink-3">Theme</Dropdown.Label>
          {themes.map((t) => (
            <Dropdown.Item key={t.v} className={item} onSelect={(e) => (e.preventDefault(), setTheme(t.v))}>
              {t.icon} {t.label} {theme === t.v ? <Check size={14} className="ml-auto" /> : null}
            </Dropdown.Item>
          ))}
          <Dropdown.Label className="eyebrow px-2 py-1 text-ink-3">Density</Dropdown.Label>
          <Dropdown.Item className={item} onSelect={(e) => (e.preventDefault(), setDensity("comfortable"))}>
            <Rows3 size={14} /> Comfortable {density === "comfortable" ? <Check size={14} className="ml-auto" /> : null}
          </Dropdown.Item>
          <Dropdown.Item className={item} onSelect={(e) => (e.preventDefault(), setDensity("compact"))}>
            <Rows4 size={14} /> Compact {density === "compact" ? <Check size={14} className="ml-auto" /> : null}
          </Dropdown.Item>
          <Dropdown.Label className="eyebrow px-2 py-1 text-ink-3">Tooltips & Help Level</Dropdown.Label>
          {HELP_LEVEL_OPTIONS.map((opt) => (
            <Dropdown.Item key={opt.value} className={item} onSelect={(e) => (e.preventDefault(), setHelpLevel(opt.value))}>
              <Sparkles size={14} /> {opt.label} {helpLevel === opt.value ? <Check size={14} className="ml-auto" /> : null}
            </Dropdown.Item>
          ))}
          <Dropdown.Separator className="my-1 h-px bg-subtle" />
          <Dropdown.Item className={item} onSelect={() => void navigate("/training")}>
            <BookOpen size={14} /> Training manual (Cliff Notes)
          </Dropdown.Item>
          <Dropdown.Item className={item} onSelect={() => void navigate("/account")}>
            <KeyRound size={14} /> Account & passkeys
          </Dropdown.Item>
          <Dropdown.Item className={item} onSelect={() => void logout(qc)}>
            <LogOut size={14} /> Sign out
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
